import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { growthKeys } from '@/data/queryKeys';
import { useNotify } from '@/data/notifications';
import {
  declareLandingPageWinner,
  fetchLandingPageDetail,
  fetchLandingPages,
  patchLandingPageBlocks,
  pauseLandingPage,
  refreshLandingPageTest,
  retireLandingPageVariant,
} from '../api';

export function useLandingPages(filters?: { status?: string; campaign?: string }) {
  return useGrowthQuery({
    queryKey: growthKeys.landingPages.list(filters ?? {}),
    queryFn: () => fetchLandingPages(filters),
  });
}

export function useLandingPageDetail(id: string, enabled = true) {
  return useGrowthQuery({
    queryKey: growthKeys.landingPages.detail(id),
    queryFn: () => fetchLandingPageDetail(id),
    enabled: enabled && Boolean(id),
  });
}

export function useLandingPageMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'landing-pages'] });

  const declareWinner = useMutation({
    mutationFn: ({ id, variantId }: { id: string; variantId: string }) =>
      declareLandingPageWinner(id, variantId),
    onSuccess: () => {
      notify.success('Winner declared');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const retireVariant = useMutation({
    mutationFn: ({ id, variantId }: { id: string; variantId: string }) =>
      retireLandingPageVariant(id, variantId),
    onSuccess: () => {
      notify.success('Variant retired');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const refreshTest = useMutation({
    mutationFn: refreshLandingPageTest,
    onSuccess: () => {
      notify.success('New test round queued');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const patchBlocks = useMutation({
    mutationFn: ({ id, blocks }: { id: string; blocks: Record<string, unknown>[] }) =>
      patchLandingPageBlocks(id, blocks),
    onSuccess: (_data, vars) => {
      notify.success('Blocks updated');
      void queryClient.invalidateQueries({ queryKey: growthKeys.landingPages.detail(vars.id) });
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const pause = useMutation({
    mutationFn: pauseLandingPage,
    onSuccess: () => {
      notify.success('Landing page paused');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  return { declareWinner, retireVariant, refreshTest, patchBlocks, pause };
}
